import type { Ref } from 'react';
import {
  Link as AriaLink,
  type LinkProps as AriaLinkProps,
} from 'react-aria-components';
import { useInRouterContext } from 'react-router-dom';
import { RouterBridge } from './RouterBridge';
import { buttonClasses, type ButtonSize, type ButtonVariant } from './Button';

export interface LinkButtonProps extends Omit<AriaLinkProps, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  ref?: Ref<HTMLAnchorElement>;
}

/**
 * A navigation link (`<a href>`) in the `Button` look: same variants and sizes.
 * Use it where the action goes to a URL; use `Button` where it runs code.
 * Inside a router, `href` navigates client-side; outside one (the root error
 * fallback) it is a plain link.
 */
export function LinkButton({
  variant = 'neutral',
  size = 'md',
  className,
  ...rest
}: LinkButtonProps) {
  const link = (
    <AriaLink {...rest} className={buttonClasses(variant, size, className)} />
  );
  return useInRouterContext() ? <RouterBridge>{link}</RouterBridge> : link;
}
